<template>
  <div class="kt-portlet mt-5 mt-md-3 mt-lg-0">
    <div
      class="kt-portlet__head kt-portlet__head kt-portlet__head--lg kt-portlet__head--noborder kt-portlet__head--break-sm flex-wrap"
    >
      <div class="kt-portlet__head-label pt-3 pt-md-0">
        <span class="kt-portlet__head-icon">
          <i class="fal fa-chart-bar"></i>
        </span>
        <h3 class="kt-portlet__head-title">Progress Report</h3>
      </div>
      <div class="kt-portlet__head-toolbar">
        <div class="kt-portlet__head-group d-flex flex-wrap justify-center">
          <v-btn
            v-show="isSuperAdmin"
            color="primary"
            dark
            depressed
            @click="sendEmailReport()"
            text
            class="mt-3 mt-md-0 mx-1"
            :loading="emailLoading"
          >
            <v-icon left>fa fa-envelope</v-icon>
            Send Progress
          </v-btn>

          <v-snackbar :timeout="0" v-model="warningEmailDialog">
            Sending Progress Report. Please wait!
            <v-progress-circular
              :size="20"
              class="mx-5"
              color="white"
              indeterminate
            ></v-progress-circular>
          </v-snackbar>

          <v-btn
            color="primary"
            dark
            depressed
            @click="checkOngoingExport()"
            text
            class="mt-3 mt-md-0 mx-1"
            :disabled="isLoading"
            :loading="exportLoading"
          >
            <v-icon left>fal fa-file-export</v-icon>
            Export All Data
          </v-btn>

          <v-snackbar :timeout="0" v-model="warningDialog">
            Progress Report is exporting. Please wait!
            <v-progress-circular
              :size="20"
              class="mx-5"
              color="white"
              indeterminate
            ></v-progress-circular>
          </v-snackbar>
        </div>
      </div>
    </div>
    <div class="kt-portlet__body">
      <div class="d-flex justify-space-between align-center">
        <div style="width: 44%">
          <div v-if="isSuperAdmin">
            <p class="subtitle-2">Company</p>
            <v-autocomplete
              item-text="name"
              item-value="id"
              solo
              dense
              label="Select Company"
              @change="selectCompany()"
              v-model="filterCompany"
              :items="companies"
              clearable
              :loading="companyLoading"
              :disabled="companyLoading"
              autocomplete="off"
            >
              <template v-slot:append-item>
                <div
                  v-if="!loadedAllCompany"
                  v-intersect="onIntersectCompany"
                  class="pa-4 primary--text"
                >
                  Loading more items ...
                </div>
              </template>
            </v-autocomplete>
          </div>
        </div>
        <div>
          <v-btn
            class="primaryMainText--text"
            color="primaryMain"
            @click="resetAllFilters()"
          >
            <v-icon left small> fa fa-sync </v-icon>
            Reset
          </v-btn>
        </div>
      </div>
      <div class="d-flex">
        <div style="width: 80%" class="mr-3">
          <p class="subtitle-2">Dealer</p>
          <v-autocomplete
            item-text="name"
            item-value="id"
            solo
            dense
            label="Dealer"
            @change="selectDealer()"
            v-model="filterSpecificDealer"
            :items="specificDealers"
            autocomplete="off"
            clearable
            :loading="specificDealerLoading"
            :disabled="specificDealerLoading || disableDealer"
          >
            <template v-slot:append-item>
              <div
                v-if="!loadedAllSpecificDealer"
                v-intersect="onIntersectSpecificDealer"
                class="pa-4 primary--text"
              >
                Loading more items ...
              </div>
            </template>
          </v-autocomplete>
        </div>
        <div style="width: 100%">
          <p class="subtitle-2">Groups</p>
          <v-select
            @change="fetchData"
            v-model="filterGroup"
            :items="groups"
            label="Select Group"
            item-text="name"
            item-value="id"
            solo
            dense
            :loading="groupLoading"
            :disabled="groupLoading"
            clearable
          />
        </div>
      </div>
      <div class="d-flex">
        <div style="width: 80%" class="mr-3">
          <p class="subtitle-2">Course</p>
          <v-autocomplete
            item-text="name"
            item-value="id"
            solo
            dense
            label="Courses"
            @change="selectCourse()"
            v-model="filterCourse"
            :items="courses"
            autocomplete="off"
            clearable
            :loading="courseLoading"
            :disabled="courseLoading"
          >
            <template v-slot:append-item>
              <div
                v-if="!loadedAllCourse"
                v-intersect="onIntersectCourse"
                class="pa-4 primary--text"
              >
                Loading more items ...
              </div>
            </template>
          </v-autocomplete>
        </div>
        <div style="width: 100%">
          <p class="subtitle-2">Module</p>
          <v-autocomplete
            item-text="name"
            item-value="id"
            solo
            dense
            label="Module"
            @change="fetchData()"
            v-model="filterModule"
            :items="modules"
            autocomplete="off"
            clearable
            hide-details
            :disabled="moduleLoading"
            :loading="moduleLoading"
            class="mb-2"
          >
            <template v-slot:append-item>
              <div
                v-if="!loadedAllModule"
                v-intersect="onIntersectModule"
                class="pa-4 primary--text"
              >
                Loading more items ...
              </div>
            </template>
          </v-autocomplete>
        </div>
      </div>
      <div class="d-flex">
        <div class="mr-3" style="width: 230px">
          <p class="subtitle-2">Date Range</p>
          <v-select
            v-model="filter.type"
            :items="dateRangetypes"
            label="Select Type"
            solo
            @change="typeChange"
            dense
            :loading="isLoading"
            :disabled="isLoading"
          />
          <v-menu
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
            v-model="customDateRangeMenu"
            ref="customDateRangeMenu"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="customDateRangeText"
                label="Select Custom Date Range"
                readonly
                v-bind="attrs"
                v-on="on"
                ref="customRange"
                v-show="filter.type === 'custom'"
                clearable
                clear-icon="fal fa-times-circle"
              />
            </template>
            <v-date-picker
              v-model="filter.dateRange"
              range
              no-title
              :max="maxDateRange"
              @change="dateSelected"
            >
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="customDateRangeMenu = false"
                :disabled="isLoading"
              >
                Cancel
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="
                  fetchData();
                  $refs.customDateRangeMenu.save(date);
                "
                :loading="isLoading"
                :disabled="isLoading"
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </div>

        <div style="width: 100%">
          <p class="subtitle-2">Users</p>
          <v-combobox
            v-model="search"
            :items="users"
            label="Users"
            item-text="name"
            item-value="name"
            multiple
            solo
            dense
            chips
            :loading="usersLoading"
            :disabled="usersLoading"
            @change="renderSearch"
            :return-object="false"
            clearable
          >
            <template v-slot:append-item>
              <div
                v-if="!loadedAllUser"
                v-intersect="onIntersectUser"
                class="pa-4 primary--text"
              >
                Loading more items ...
              </div>
            </template>
          </v-combobox>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :loading="isLoading"
        loading-text="Loading... Please wait"
        :items="statistics"
        :options.sync="options"
        :server-items-length="pagination.total"
        :items-per-page="pagination.per_page"
        :footer-props="{ disablePagination: paginationDisable }"
        class="elevation-1"
        :class="
          statistics.length < 10 && statistics.length > 0
            ? ''
            : 'progress-report-table'
        "
      >
        <template
          v-for="h in headers"
          v-slot:[`header.${h.value}`]="{ header }"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on">{{ h.text }}</span>
            </template>
            <span>{{ h.tooltip }}</span>
          </v-tooltip>
        </template>
        <template #item.units_completed="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                target="_blank"
                color="primary"
                text
                v-bind="attrs"
                v-on="on"
                @click="
                  getCompleted(
                    item.id,
                    'date_completed',
                    title_list[0],
                    item.units_completed
                  )
                "
              >
                {{ item.units_completed }}
              </v-btn>
            </template>
            <span>{{ item.units_completed == 0 ? 'No Result' : 'View' }}</span>
          </v-tooltip>
        </template>
        <template #item.certificates="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                target="_blank"
                color="primary"
                text
                v-bind="attrs"
                v-on="on"
                @click="
                  getCertificate(item.id, title_list[1], item.certificates)
                "
              >
                {{ item.certificates }}
              </v-btn>
            </template>
            <span>{{ item.certificates == 0 ? 'No Result' : 'View' }}</span>
          </v-tooltip>
        </template>
        <template #item.unfinished_assigned_module="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                target="_blank"
                color="primary"
                text
                v-bind="attrs"
                v-on="on"
                @click="
                  getUnfinishedModule(
                    item.id,
                    title_list[2],
                    item.unfinished_assigned_module
                  )
                "
              >
                {{ item.unfinished_assigned_module }}
              </v-btn>
            </template>
            <span>{{
              item.unfinished_assigned_module == 0 ? 'No Result' : 'View'
            }}</span>
          </v-tooltip>
        </template>
        <template #item.unfinished_assigned_playlist="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                target="_blank"
                color="primary"
                text
                v-bind="attrs"
                v-on="on"
                @click="
                  getUnfinishedPlaylist(
                    item.id,
                    title_list[3],
                    item.unfinished_assigned_playlist
                  )
                "
              >
                {{ item.unfinished_assigned_playlist }}
              </v-btn>
            </template>
            <span>{{
              item.unfinished_assigned_playlist == 0 ? 'No Result' : 'View'
            }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
      <unit-modal
        :key="'unit-' + unitModalKey"
        v-model="showUnitModal"
        :units="units"
        :params="params"
        :title="title"
        :userId="userId"
      />

      <module-modal
        :key="'module-' + moduleModalKey"
        v-model="showModuleModal"
        :certificates="certificates"
        :title="title"
        :userId="userId"
      />
      <export-modal
        v-model="showExportModal"
        @exportProceed="exportData"
        :onGoingExport="onGoingExport"
      />
    </div>
  </div>
</template>
<script>
import EventBus from '@/plugins/eventBus';
import UnitModal from './UnitModal.vue';
import ModuleModal from './ModuleModal.vue';
import ExportModal from './ExportModal.vue';
import moment from 'moment';
import { some as _some } from 'lodash-es';

function defaultFilter() {
  let dateRange = 'YYYY-MM-DD';
  return {
    type: 'All',
    dateRange: [moment().format(dateRange), moment().format(dateRange)],
    categories: [],
    users: [],
  };
}

export default {
  name: 'ProgressReport',
  props: ['name', 'type', 'dealerId', 'specificDealerId'],
  components: {
    UnitModal,
    ModuleModal,
    ExportModal,
  },

  data() {
    return {
      onProcess: false,
      warningDialog: false,
      warningEmailDialog: false,
      selectSelect: [],
      params: {
        isPlaylist: false,
        isCompleted: false,
      },
      isLoading: false,
      companyLoading: false,
      specificDealerLoading: false,
      groupLoading: false,
      exportLoading: false,
      search: '',
      showUnitModal: false,
      unitModalKey: 0,
      moduleModalKey: 0,
      showModuleModal: false,
      showExportModal: false,
      onGoingExport: false,
      userId: [],
      title: '',
      title_list: [
        {
          title: 'Units/Videos | Answered Quiz / Marked as Completed',
          value: 'units_completed',
        },
        {
          title: 'Units/Videos | Certificates',
          value: 'certificates',
        },
        {
          title: 'Units/Videos | Unfinished in Assigned Module',
          value: 'unfinished_assigned_module',
        },
        {
          title: 'Units/Videos | Unfinished in Assigned Playlist',
          value: 'unfinished_assigned_playlist',
        },
      ],
      units: [],
      certificates: [],
      headers: [
        {
          text: 'Name',
          tooltip: 'Name of Salesperson',
          align: 'start',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Completed Units',
          tooltip: 'Videos / units marked as completed or completed the quiz.',
          value: 'units_completed',
          sortable: true,
          align: 'center',
        },
        {
          text: 'Total Certificates',
          tooltip:
            'Certificates acquired from Modules, Series and Assigned Playlist',
          value: 'certificates',
          align: 'center',
        },
        {
          text: 'Unfinished Units/Videos in Assigned Module',
          tooltip: 'Unfinished units/videos from modules assigned to user',
          value: 'unfinished_assigned_module',
          align: 'center',
        },
        {
          text: 'Unfinished Units/Videos in Assigned Playlist',
          tooltip: 'Unfinished units/videos from playlists assigned to user',
          value: 'unfinished_assigned_playlist',
          align: 'center',
        },
      ],
      statistics: [],
      pagination: {
        total: 1,
        per_page: 10,
        current_page: 1,
        search: '',
        sortBy: '',
        sortDesc: false,
      },
      options: {},
      paginationDisable: false,
      timeout: null,

      disableDealer: false,
      specificDealers: [],
      filterSpecificDealer: '',
      specificDealerSetting: {
        page: 1,
        per_page: 20,
        total: '',
      },
      groups: [],
      filterGroup: '',
      companies: [],
      filterCompany: '',
      companySetting: {
        page: 1,
        per_page: 20,
        total: '',
      },

      users: [],
      selectedUsers: [],
      usersLoading: false,
      userSetting: {
        page: 1,
        per_page: 20,
        total: '',
      },

      courses: [],
      filterCourse: '',
      courseLoading: false,
      courseSetting: {
        page: 1,
        per_page: 20,
        total: '',
      },

      modules: [],
      filterModule: '',
      moduleLoading: false,
      moduleSetting: {
        page: 1,
        per_page: 20,
        total: '',
      },

      emailLoading: false,

      customDateRangeMenu: false,
      filter: defaultFilter(),
      maxDateRange: moment().endOf('day').format('YYYY-MM-DD'),
      dateRangetypes: ['All', '90days', '60days', '30days', '7days', 'custom'],
    };
  },
  computed: {
    user() {
      return this.$auth.user();
    },
    isSuperAdmin() {
      return _some(this.user.roles, { name: 'super-administrator' });
    },
    isCompanyAdmin() {
      return _some(this.user.roles, { name: 'account-manager' });
    },
    isDealerAdmin() {
      let dealerRole = _some(this.user.roles, {
        name: 'specific-dealer-manager',
      });
      return !this.isSuperAdmin && !this.isCompanyAdmin && dealerRole;
    },
    customDateRangeText() {
      const date = this.filter.dateRange;
      const dateFromText =
        typeof date[0] !== 'undefined'
          ? moment(date[0]).format('MM/DD/YYYY')
          : '';
      const dateToText =
        typeof date[1] !== 'undefined'
          ? moment(date[1]).format('MM/DD/YYYY')
          : '';

      return `${dateFromText} ~ ${dateToText}`;
    },
    isHudsonAcademy() {
      return window.location.host == 'thehudsonacademy.com';
    },
    loadedAllSpecificDealer() {
      return (
        this.specificDealers.length >=
        parseInt(this.specificDealerSetting.total)
      );
    },
    loadedAllCompany() {
      return this.companies.length >= parseInt(this.companySetting.total);
    },
    loadedAllUser() {
      return this.users.length >= parseInt(this.userSetting.total);
    },
    loadedAllCourse() {
      return this.courses.length >= parseInt(this.courseSetting.total);
    },
    loadedAllModule() {
      return this.modules.length >= parseInt(this.moduleSetting.total);
    },
  },
  watch: {
    options: {
      handler() {
        this.changeTable();
      },
      deep: true,
    },
    search() {
      console.log(this.search);
    },
    filterSpecificDealer(val) {
      if (val && this.isDealerAdmin) {
        this.fetchData();
      }
    },
  },
  mounted() {
    if (this.type != undefined && this.type == 0) {
      this.search = [this.name];
    }

    if (this.specificDealerId) {
      this.filterSpecificDealer = parseInt(this.specificDealerId);
    }
    this.filterCompany = this.dealerId
      ? parseInt(this.dealerId)
      : this.user.dealer_id;

    this.renderSearch();
  },
  created() {
    if (this.isSuperAdmin) {
      this.fetchCompanies();
    } else {
      this.fetchGroups();
      this.fetchUsers();
    }

    this.fetchData();
    this.fetchSpecificDealers();
    this.fetchCourse();
    this.fetchModule();

    EventBus.$on('reset', () => {
      this.resetAllFilters();
    });
  },
  methods: {
    sendEmailReport() {
      let company_id = this.filterCompany
        ? this.filterCompany
        : this.user.dealer_id;

      this.emailLoading = true;
      this.warningEmailDialog = true;
      this.axios
        .get(`/client/reports/send-managers-email/report/${company_id}`)
        .then((response) => {
          this.emailLoading = false;
          this.warningEmailDialog = false;
        })
        .catch((error) => {
          this.emailLoading = false;
          this.warningEmailDialog = false;
        });
    },
    typeChange() {
      if (this.filter.type === 'custom') {
        this.$nextTick(() => (this.customDateRangeMenu = true));
        return;
      }

      this.fetchData();
    },
    dateSelected(value) {
      const dateFrom = moment(value[0]);
      const dateTo = moment(value[1]);
      const dateFormat = 'YYYY-MM-DD';

      // check if the selected "date from" is greater than
      // the "date to", if yes
      // then swap it
      if (dateFrom.isAfter(dateTo)) {
        this.filter.dateRange = [
          dateTo.format(dateFormat),
          dateFrom.format(dateFormat),
        ];
      }
    },
    renderSearch() {
      clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        this.options.page = 1;
        this.changeTable();
      }, 1000);
    },
    fetchData() {
      if (this.isDealerAdmin && this.filterSpecificDealer == '') {
        this.isLoading = true;
        return false;
      }

      this.isLoading = true;
      this.paginationDisable = true;
      this.statistics = [];

      this.$http
        .get('/client/reports/lms/progress', {
          params: {
            per_page: this.pagination.per_page,
            current_page: this.pagination.current_page,
            sortBy: this.pagination.sortBy,
            sortDesc: this.pagination.sortDesc,
            search: this.search,
            dateRange: this.filter.dateRange,
            dateRangeType: this.filter.type,
            specificDealer: this.filterSpecificDealer,
            group: this.filterGroup,
            company: this.filterCompany,
            course: this.filterCourse,
            module: this.filterModule,
          },
        })
        .then((data) => {
          this.statistics = data.data.data;
          this.pagination.total = data.data.total;
          this.pagination.current_page = data.data.current_page;
          this.pagination.per_page = data.data.per_page;
          this.isLoading = false;
          this.paginationDisable = false;
        });
    },

    //Dealer Setup
    onIntersectCompany() {
      if (!this.loadedAllCompany) {
        this.companySetting.page += 1;
        this.fetchCompanies();
      }
    },
    onIntersectSpecificDealer() {
      if (!this.loadedAllSpecificDealer) {
        this.specificDealerSetting.page += 1;
        this.fetchSpecificDealers();
      }
    },
    fetchSpecificDealers() {
      if (this.specificDealerSetting.page == 1) {
        this.specificDealerLoading = true;
        this.paginationDisable = true;
      }

      this.$http
        .get(`dealer/${this.user.dealer_id}/specific-dealers-paginate`, {
          params: {
            per_page: this.specificDealerSetting.per_page,
            current_page: this.specificDealerSetting.page,
            company_id: this.filterCompany,
          },
        })
        .then((result) => {
          this.specificDealers = [...this.specificDealers, ...result.data.data];
          this.specificDealerSetting.total = result.data.total;
          this.specificDealerLoading = false;
          this.paginationDisable = false;

          if (this.isDealerAdmin) {
            this.filterSpecificDealer = this.user.specific_dealer_id;
            this.disableDealer = true;
            this.selectDealer;
          } else {
            this.disableDealer = false;
          }
        });
    },
    fetchCompanies() {
      if (this.companySetting.page == 1) {
        this.companyLoading = true;
        this.paginationDisable = true;
      }

      this.$http
        .get(`dealers/list/paginate`, {
          params: {
            per_page: this.companySetting.per_page,
            current_page: this.companySetting.page,
          },
        })
        .then((result) => {
          this.companies = [...this.companies, ...result.data.data];
          this.companySetting.total = result.data.total;
          this.companyLoading = false;
          this.paginationDisable = false;

          if (result.data.page == 1) {
            this.filterCompany = this.user.dealer_id;
          }
          this.fetchGroups();
          this.fetchUsers();
        });
    },
    selectCompany() {
      this.specificDealers = [];
      this.search = '';
      this.users = [];
      this.userSetting.page = 1;
      this.userSetting.total = '';

      this.filterSpecificDealer = '';
      this.specificDealerSetting.page = 1;
      this.specificDealerSetting.total = '';

      this.courses = '';
      this.filterCourse = '';
      this.courseSetting.page = 1;
      this.courseSetting.total = '';

      this.modules = '';
      this.filterModule = '';
      this.moduleSetting.page = 1;
      this.moduleSetting.total = '';

      this.fetchGroups();
      this.fetchData();
      this.fetchUsers();
      this.fetchSpecificDealers();
      this.fetchCourse();
      this.fetchModule();
    },
    selectDealer() {
      this.search = '';
      (this.users = []), (this.userSetting.page = 1);
      this.userSetting.total = '';

      this.courses = '';
      this.filterCourse = '';
      this.courseSetting.page = 1;
      this.courseSetting.total = '';

      this.fetchUsers();
      this.fetchData();
      this.fetchCourse();
    },
    fetchGroups() {
      this.groupLoading = true;
      this.paginationDisable = true;
      this.groups = [];
      let company_id = this.filterCompany
        ? this.filterCompany
        : this.user.dealer_id;
      this.$http.get(`get-groups-dealer/${company_id}`).then((data) => {
        this.groups = data.data;
        this.groupLoading = false;
        this.paginationDisable = false;
      });
    },

    onIntersectUser() {
      if (!this.loadedAllUser) {
        this.userSetting.page += 1;
        this.fetchUsers();
      }
    },
    fetchUsers() {
      if (this.userSetting.page == 1) {
        this.usersLoading = true;
        this.paginationDisable = true;
      }

      let company_id = this.filterCompany
        ? this.filterCompany
        : this.user.dealer_id;

      this.$http
        .get(`get-users-by-company/${company_id}`, {
          params: {
            per_page: this.userSetting.per_page,
            current_page: this.userSetting.page,
            dealer_id: this.filterSpecificDealer,
          },
        })
        .then((result) => {
          this.users = [...this.users, ...result.data.data];
          this.userSetting.total = result.data.total;
          this.usersLoading = false;
          this.paginationDisable = false;
        });
    },
    getCompleted(id, prop, title, count) {
      if (count == 0) return false;
      this.isLoading = true;
      this.$http
        .get(`/client/reports/lms/progress/get-completed/${id}`, {
          params: {
            prop: prop,
            dateRange: this.filter.dateRange,
            dateRangeType: this.filter.type,
            course: this.filterCourse,
            module: this.filterModule,
          },
        })
        .then((data) => {
          this.isLoading = false;
          this.params.isCompleted = true;
          this.showModalUnit(id, title, data.data);
        });
    },

    getUnfinishedModule(id, title, count) {
      if (count == 0) return false;
      this.isLoading = true;
      this.$http
        .get(`/client/reports/lms/progress/get-unfinished-module/${id}`, {
          params: {
            dateRange: this.filter.dateRange,
            dateRangeType: this.filter.type,
            course: this.filterCourse,
            module: this.filterModule,
          },
        })
        .then((data) => {
          this.isLoading = false;
          this.showModalUnit(id, title, data.data);
        });
    },

    getUnfinishedPlaylist(id, title, count) {
      if (count == 0) return false;
      this.isLoading = true;
      this.$http
        .get(`/client/reports/lms/progress/get-unfinished-playlist/${id}`, {
          params: {
            dateRange: this.filter.dateRange,
            dateRangeType: this.filter.type,
            course: this.filterCourse,
            module: this.filterModule,
          },
        })
        .then((data) => {
          this.isLoading = false;
          this.params.isPlaylist = true;
          this.showModalUnit(id, title, data.data);
        });
    },

    getCertificate(id, title, count) {
      if (count === 0) {
        return false;
      }

      this.isLoading = true;
      this.$http
        .get(`/client/reports/lms/progress/get-certificate/${id}`, {
          params: {
            dateRange: this.filter.dateRange,
            dateRangeType: this.filter.type,
            course: this.filterCourse,
            module: this.filterModule,
          },
        })
        .then((data) => {
          this.isLoading = false;
          this.showModalModule(id, title, data.data);
        });
    },

    changeTable() {
      window.scrollTo(0, 0);
      this.pagination.current_page = this.options.page;

      this.pagination.sortBy =
        this.options.sortBy !== undefined ? this.options.sortBy[0] : '';
      this.pagination.sortDesc =
        this.options.sortDesc !== undefined ? this.options.sortDesc[0] : '';

      this.fetchData();
    },

    showModalUnit(id, title, items) {
      this.unitModalKey += 1;
      this.title = title;
      this.units = items;
      this.userId = id;
      this.showUnitModal = true;
    },
    showModalModule(id, title, items) {
      this.moduleModalKey += 1;
      this.title = title;
      this.certificates = items;
      this.userId = id;
      this.showModuleModal = true;
    },
    checkOngoingExport() {
      this.exportLoading = true;
      this.$http
        .get(`/client/reports/lms/progress/check-ongoing-export`)
        .then((result) => {
          this.exportLoading = false;
          this.onGoingExport = result.data;
          this.showExportModal = true;
        });
    },
    exportData() {
      this.exportLoading = true;
      this.showExportModal = false;
      this.warningDialog = true;
      this.$http
        .get(`/client/reports/lms/progress/call-job`, {
          params: {
            dateRange: this.filter.dateRange,
            dateRangeType: this.filter.type,
            search: this.search,
            specificDealer: this.filterSpecificDealer,
            group: this.filterGroup,
            company: this.filterCompany,
            course: this.filterCourse,
            module: this.filterModule,
          },
        })
        .then((data) => {
          this.warningDialog = false;
          if (data.data.success) {
            this.$notify('success', data.data.message, false, 5000);
          } else {
            this.$notify('error', data.data.message, false, 3000);
          }
          this.exportLoading = false;
        });
    },

    resetAllFilters() {
      this.statistics = [];
      this.filterGroup = '';
      this.filterCompany = this.user.dealer_id;

      this.search = '';
      this.users = [];
      this.userSetting.page = 1;
      this.userSetting.total = '';
      this.fetchUsers();

      this.specificDealers = [];
      this.filterSpecificDealer = '';
      this.specificDealerSetting.page = 1;
      this.specificDealerSetting.total = '';
      this.fetchSpecificDealers();

      this.fetchGroups();

      this.filter.type = 'All';
      this.options.page = 1;
      this.changeTable();
    },

    //Courses and Module Setup
    selectCourse() {
      this.modules = [];
      this.moduleSetting.total = '';
      this.moduleSetting.page = 1;
      this.fetchModule();
      this.fetchData();
    },
    onIntersectCourse() {
      if (!this.loadedAllCourse) {
        this.courseSetting.page += 1;
        this.fetchCourse();
      }
    },
    fetchCourse() {
      if (this.courseSetting.page == 1) {
        this.courseLoading = true;
        this.paginationDisable = true;
      }

      this.$http
        .get(`lms-manager/courses/get-paginate`, {
          params: {
            per_page: this.courseSetting.per_page,
            current_page: this.courseSetting.page,
            company: this.filterCompany,
            dealer: this.filterSpecificDealer,
          },
        })
        .then((result) => {
          this.courses = [...this.courses, ...result.data.data];
          this.courseSetting.total = result.data.total;
          this.courseLoading = false;
          this.paginationDisable = false;
        });
    },

    onIntersectModule() {
      if (!this.loadedAllModule) {
        this.moduleSetting.page += 1;
        this.fetchModule();
      }
    },
    fetchModule() {
      if (this.moduleSetting.page == 1) {
        this.moduleLoading = true;
        this.paginationDisable = true;
      }

      this.$http
        .get(`lms-manager/modules/get-paginate`, {
          params: {
            per_page: this.moduleSetting.per_page,
            current_page: this.moduleSetting.page,
            course_id: this.filterCourse,
            company: this.filterCompany,
          },
        })
        .then((result) => {
          this.modules = [...this.modules, ...result.data.data];
          this.moduleSetting.total = result.data.total;
          this.moduleLoading = false;
          this.paginationDisable = false;
        });
    },
  },
};
</script>
<style lang="stylus">
.item-link
  text-decoration underline
.v-snack__content
  justify-content center
.progress-report-table
  .v-data-table__wrapper
    min-height 40rem
</style>
